
// Parsing primitives shared by the AMI parser in `./ami`.
//
// This file used to hold a second, complete parse pipeline — its own column
// detection, unit detection, interval resolution and Excel reader — reachable
// through `parserRouter.ts` behind `const USE_NEW_PARSER = true`. It had not run
// since the June 2026 rebuild, and it carried none of the hardening the modular
// parser gained: no Layer 0 invariants, no plausibility checks, its own separate
// column scoring. Dead code that produces load calculations is a liability, and
// one flipped constant was all that stood between it and a permit application.
// Removed August 2026, along with the router that selected between the two.
//
// What is left is primitives, not a pipeline: types, timestamp parsing, header
// pattern matching, RFC 4180 tokenizing, encoding detection, numeric parsing and
// interval detection. There is exactly one parser, in `./ami/parser.ts`, and it
// is the only thing that turns a file into readings.

export interface ParsedDataPoint {
  timestamp: string;
  kw: number;
}

export interface ParseResult {
  data: ParsedDataPoint[];
  errors: string[];
  warnings: string[];
  detectedFormat: string;
  columnSelectionLowConfidence?: boolean;
  generationColumnDetected?: boolean;
  grossPeakKW?: number;
  metadata?: {
    intervalMinutes: number | null;
    powerSource: "kw" | "derived_kwh" | "mixed_row_units" | "unknown";
    rowsIgnored: number;
    rowsRejected: number;
    dataQuality: string[];
    columnSelectionLowConfidence?: boolean;
    generationColumnDetected?: boolean;
    grossPeakKW?: number;
    // ── Layer 2 (observational; does not affect parsing) ────────────────────
    // Post-parse plausibility flags and the scalar telemetry summary the AMI
    // Data tab forwards to parser_events. Both are optional and additive.
    plausibilityFlags?: Array<{ check: string; severity: 'verify' | 'info'; message: string; detail: Record<string, unknown> }>;
    telemetry?: Record<string, unknown>;
    /** Runs of readings the source marks as utility-estimated (ami/estimatedReadings.ts). */
    estimatedRuns?: Array<{ start: string; end: string }>;
  };
}

// ─── Helpers used by parseExcelFlexible ──────────────────────────────────────

// Interpret bare wall-clock components at a FIXED (UTC) offset. Bare timestamp
// strings carry no timezone, so parsing them in the runtime's local zone lets a
// DST transition corrupt the instant: at the March spring-forward the
// non-existent 02:00–02:59 local readings roll forward to 03:00, colliding with
// that hour's genuine readings and silently collapsing distinct interval data.
// Keying off a fixed offset keeps textually-distinct wall-clock strings distinct
// as instants, while textually-identical strings (a genuine November fall-back
// repeat in a true local-clock column) still collapse — that ambiguity lives in
// the source data itself, not in the runtime.
function utcWallClock(
  year: number, month1: number, day: number,
  hour = 0, minute = 0, second = 0,
): Date {
  return new Date(Date.UTC(year, month1 - 1, day, hour, minute, second));
}

/**
 * An Excel date serial as a fixed-UTC wall-clock instant.
 *
 * Excel stores a date cell as days since 1899-12-30 (or 1904-01-01 in a
 * 1904-system workbook). SheetJS can turn that into a JS Date, but it does so in
 * the runtime's local zone, which is the same defect utcWallClock() exists to
 * avoid: the instant then depends on the machine that read the file, and a
 * reading that falls in the spring-forward hour has no local representation at
 * all. Converting the serial arithmetically keeps the wall-clock the utility
 * wrote, on every machine, in every zone.
 *
 * Time-of-day is rounded to the nearest second. A serial is a binary fraction of
 * a day, so 19:15 arrives as 19:14:59.999 unless it is rounded — which is how
 * every timestamp in an xlsx upload used to end in .999Z.
 */
export function excelSerialToUtcWallClock(serial: number, date1904 = false): Date | null {
  if (!Number.isFinite(serial)) return null;
  const epoch = date1904 ? Date.UTC(1904, 0, 1) : Date.UTC(1899, 11, 30);
  const days = Math.floor(serial);
  const seconds = Math.round((serial - days) * 86400);
  const d = new Date(epoch + days * 86400000 + seconds * 1000);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Header text with its word separators made visible to `\b`.
 *
 * `\b` treats underscore as a word character, so `\btime\b` matches "ending time"
 * and not "ending_time". That single fact is why a Burlington Electric export
 * whose date column is named `interval_ending_time` scored zero as a timestamp
 * header and was refused (2026-09-08). Snake_case, kebab-case, dotted, slashed
 * and CamelCase headers all become space-separated lower-case words, so every
 * pattern sees the tokens the space-separated spelling would have given it.
 *
 *   "interval_ending_time" → "interval ending time"
 *   "IntervalStart"        → "interval start"
 *   "Date/Time"            → "date time"
 *
 * CamelCase is split only after two or more lower-case letters, so unit tokens
 * such as kW, kWh and kVA stay whole: "Demand (kW)" must not become "demand (k w)".
 */
export function headerWords(header: string): string {
  return header
    .replace(/([a-z]{2}|[0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-./]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function parseFlexibleTimestamp(input: string): Date | null {
  const trimmed = input.trim();

  const formats = [
    // Explicit ISO 8601 with space separator and UTC offset: "2025-05-29 00:00:00-04:00"
    () => {
      const m = trimmed.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})([-+]\d{2}:\d{2}|Z)$/);
      return m ? new Date(`${m[1]}T${m[2]}${m[3]}`) : null;
    },
    // Bare ISO-like (zero-padded, space or 'T' separator, NO offset) → fixed UTC.
    // Sits before the permissive `new Date(trimmed)` below, which would otherwise
    // interpret this shape in the runtime's DST-observing local zone.
    () => {
      const m = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
      if (m) {
        const [, y, mo, d, h, mi, s = '0'] = m;
        return utcWallClock(+y, +mo, +d, +h, +mi, +s);
      }
      return null;
    },
    () => {
      const match = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})-(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (match) {
        const [, year, month, day, hours, minutes, seconds = '0'] = match;
        return utcWallClock(+year, +month, +day, +hours, +minutes, +seconds);
      }
      return null;
    },
    () => {
      const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?$/i);
      if (match) {
        const [, month, day, year, hours, minutes, seconds = '0', ampm] = match;
        const fullYear = year.length === 2 ? (parseInt(year) < 50 ? '20' + year : '19' + year) : year;
        let hour = parseInt(hours);
        if (ampm) {
          if (ampm.toUpperCase() === 'PM' && hour !== 12) hour += 12;
          if (ampm.toUpperCase() === 'AM' && hour === 12) hour = 0;
        }
        return utcWallClock(+fullYear, +month, +day, hour, +minutes, +seconds);
      }
      return null;
    },
    () => {
      const match = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (match) {
        const [, year, month, day, hours, minutes, seconds = '0'] = match;
        return utcWallClock(+year, +month, +day, +hours, +minutes, +seconds);
      }
      return null;
    },
    () => {
      const match = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (match) {
        const [, year, month, day, hours, minutes, seconds = '0'] = match;
        return utcWallClock(+year, +month, +day, +hours, +minutes, +seconds);
      }
      return null;
    },
    () => {
      const match = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{2,4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (match) {
        const [, month, day, year, hours, minutes, seconds = '0'] = match;
        const fullYear = year.length === 2 ? (parseInt(year) < 50 ? '20' + year : '19' + year) : year;
        return utcWallClock(+fullYear, +month, +day, +hours, +minutes, +seconds);
      }
      return null;
    },
    () => {
      const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
      if (match) {
        const [, month, day, year] = match;
        const fullYear = year.length === 2 ? (parseInt(year) < 50 ? '20' + year : '19' + year) : year;
        return utcWallClock(+fullYear, +month, +day);
      }
      return null;
    },
    () => {
      const match = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (match) {
        const [, year, month, day] = match;
        return utcWallClock(+year, +month, +day);
      }
      return null;
    },
    () => {
      const parsed = parseFloat(trimmed);
      if (!isNaN(parsed) && parsed > 40000 && parsed < 60000) {
        return excelSerialToUtcWallClock(parsed);
      }
      return null;
    },
    // LAST on purpose. `new Date` resolves a naive wall-clock string in the
    // runtime's own zone, so it must never get first refusal on a shape one of
    // the utcWallClock branches above handles. It sat third in this list and
    // shadowed every US-format branch below it: "05/01/2026 00:00" came out as
    // 04:00Z in America/New_York and 00:00Z in UTC, meaning the same export
    // produced different peak timestamps in the browser and in a Netlify
    // function, with the offset changing across a DST boundary inside one file.
    // Kept as the final fallback for shapes no explicit branch claims.
    () => new Date(trimmed),
  ];

  for (const formatParser of formats) {
    try {
      const result = formatParser();
      if (result && !isNaN(result.getTime())) {
        return result;
      }
    } catch {
      continue;
    }
  }

  return null;
}

// ─── kWh header detection ─────────────────────────────────────────────────────

export function isKWhHeader(header: string | null): boolean {
  if (!header) return false;
  const stripped = header.toLowerCase().replace(/[^a-z0-9]/g, '');
  const normalized = stripped
    .replace(/perhour/g, 'h')
    .replace(/perhr/g, 'h')
    .replace(/hour/g, 'h');
  return normalized.includes('kwh') || normalized.includes('kilowatth');
}

// ─── Column scoring ───────────────────────────────────────────────────────────

export const KW_EXCLUSION_PATTERNS = [
  /\bsp\b/i,
  /\bid\b/i,
  /\baccount\b/i,
  /\bmeter\b/i,
  /\bserial\b/i,
  /\bvolt/i,
  /\bamp/i,
  /\bpf\b/i,
  /power.{0,2}factor/i,
  /\breactive\b/i,
  /\bvar\b/i,
  /kvar/i,
  /\btherm/i,
  /\bccf\b/i,
  /\bbtu\b/i,
  /\bgenerat/i,
  /\bexport/i,
  // Non-demand columns that parseFloat would silently mis-score
  /\bweather\b/i,
  /\breceived\b/i,
  /\bcost/i,
  /\baddress\b/i,
  /\bname\b/i,
  /\bservice\b/i,
  /\bunits?\b/i,
  /\btype\b/i,
  // Weather/environmental columns appended by GMP "meter data + weather" exports.
  // NOTE: \b fails across underscores (underscore is a word char), so these
  // patterns deliberately avoid word boundaries adjacent to snake_case tokens.
  /dew[\s_-]*point/i,
  /bulb/i,
  /humidity/i,
  /pressure/i,
  /wind/i,
  /cloud/i,
  /enthalpy/i,
  /temperature/i,
  /saturation/i,
  /specific[\s_-]*volume/i,
  /offset/i,
  /quality/i,
];

export const KW_TIER1_PATTERNS = [
  /\bkw\b/i,
  /\bkva\b/i,
  /\bkilowatt\b/i,
  /\bdemand\b/i,
  /\bnet\b/i,
  /\bdelivered\b/i,
  /interval[\s_-]*demand/i,
  /avg[\s_-]*demand/i,
  /average[\s_-]*demand/i,
  // snake_case kW headers, e.g. power_kW / net_power_kW — \bkw\b fails because
  // underscore is a word character, so match kw as an underscore/space/hyphen-delimited token
  /(?:^|[_\s-])kw(?:[_\s-]|$)/i,
];

export const KW_TIER2_PATTERNS = [
  /\bkwh\b/i,
  /\bpower\b/i,
  /\busage\b/i,
  /\bconsumption\b/i,
  /\breading\b/i,
  /\bvalue\b/i,
  /\buse\b/i,
  /\benergy\b/i,
];

export function isCumulativeRegister(values: number[]): boolean {
  if (values.length < 10) return false;

  let nonDecreasing = 0;
  for (let i = 1; i < values.length; i++) {
    if (values[i] >= values[i - 1]) nonDecreasing++;
  }
  const monotonicRatio = nonDecreasing / (values.length - 1);
  if (monotonicRatio < 0.95) return false;

  const max = Math.max(...values);
  if (max <= 10000) return false;

  const sorted = [...values].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  const range = max - Math.min(...values);
  if (range <= median * 100) return false;

  return true;
}

export function scoreTimestampHeader(header: string): number {
  const h = headerWords(header);
  if (/\bstart[\s_-]*time\b/.test(h)) return 100;
  if (/\btimestamp\b/.test(h) || /\bdate[\s\/\-_]*time\b/.test(h)) return 90;
  if (/start\b/.test(h)) return 85;   // covers "IntervalStart", "startdate", etc.
  if (/\bend[\s_-]*time\b/.test(h)) return 70;
  if (/\btime\b/.test(h)) return 60;
  if (/\bdate\b/.test(h)) return 20;
  return 0;
}

function detectDelimiter(firstLine: string): string {
  const candidates = [",", "\t", ";"];
  let best = ",";
  let max = 0;

  for (const d of candidates) {
    // splitRow, not String.split — a delimiter inside a quoted field is not a
    // field boundary and must not count toward the score.
    const count = splitRow(firstLine, d).length;
    if (count > max) {
      max = count;
      best = d;
    }
  }
  return best;
}

/**
 * RFC 4180 field splitter.
 *
 * The naive `line.split(delimiter)` this replaced shifted every column to the
 * right of a quoted field that contained the delimiter, silently and without
 * error. National Grid puts the whole service address in one quoted column
 * ("12 POINT VIEW DR,EAST GREENBUSH,NY,12061"), which pushed the ZIP into the
 * `interval_start` position; "12061" then read as the year 12061 and the entire
 * export collapsed onto a single out-of-range timestamp. Eversource quotes the
 * account name ("MARIN, JACOB F / ..."), a one-column shift that swapped
 * IntervalStart for IntervalEnd on every reading.
 *
 * A quoted field keeps its interior verbatim — delimiters, leading and trailing
 * spaces — and `""` unescapes to one `"`. Unquoted fields are trimmed, which is
 * what every caller already relied on. A quote that appears after the field has
 * started (`24" duct`) is data, not a delimiter, and is kept as-is.
 */
export function splitRow(line: string, delimiter: string): string[] {
  const fields: string[] = [];
  let field = "";
  let inQuotes = false;
  let wasQuoted = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
        continue;
      }
      field += ch;
      continue;
    }

    // An opening quote only counts at the start of a field; whitespace ahead of
    // it is insignificant padding.
    if (ch === '"' && field.trim() === "") {
      inQuotes = true;
      wasQuoted = true;
      field = "";
      continue;
    }

    if (ch === delimiter) {
      fields.push(wasQuoted ? field : field.trim());
      field = "";
      wasQuoted = false;
      continue;
    }

    field += ch;
  }

  fields.push(wasQuoted ? field : field.trim());
  return fields;
}

/**
 * Split CSV text into records, treating a newline inside a quoted field as part
 * of the field rather than the end of a row. Quotes are left in place for
 * splitRow to interpret, so the two functions agree on what a field boundary is.
 *
 * The delimiter is needed to recognise where a field starts: without it a lone
 * unbalanced quote anywhere in the file would flip the quoting state and swallow
 * every remaining row.
 */
export function splitCSVRecords(text: string, delimiter: string): string[] {
  const records: string[] = [];
  let current = "";
  let inQuotes = false;
  let atFieldStart = true;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        current += '""';
        i++;
        continue;
      }
      if (ch === '"') {
        inQuotes = false;
        atFieldStart = false;
      }
      current += ch;
      continue;
    }

    if (ch === '"' && atFieldStart) {
      inQuotes = true;
      current += ch;
      continue;
    }

    if (ch === delimiter) {
      current += ch;
      atFieldStart = true;
      continue;
    }

    if (ch === "\r" || ch === "\n") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      records.push(current);
      current = "";
      atFieldStart = true;
      continue;
    }

    if (ch !== " " && ch !== "\t") atFieldStart = false;
    current += ch;
  }

  if (current !== "") records.push(current);
  return records;
}

/**
 * Decode a delimited text file whose encoding we do not know in advance.
 *
 * Utility exports are not all UTF-8. Two real customer files — a tab-delimited
 * meter export supplied for separate projects — are UTF-16LE, and decoding them
 * as UTF-8 turns every column into mojibake interleaved with NUL bytes. The
 * parser then found no timestamp column and sent the user to the manual column
 * picker, which cannot help: there is nothing legible to pick. The file was
 * unreadable and the stated reason was wrong.
 *
 * A byte-order mark settles it outright. Failing that, UTF-16 text in a Latin
 * script is full of NUL bytes at predictable parity, which UTF-8 text never is.
 */
export function decodeTextFile(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);

  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return new TextDecoder('utf-16le').decode(bytes.subarray(2));
  }
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return new TextDecoder('utf-16be').decode(bytes.subarray(2));
  }
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return new TextDecoder('utf-8').decode(bytes.subarray(3));
  }

  // No BOM. Sample the head for NUL bytes and their parity.
  const sample = bytes.subarray(0, Math.min(bytes.length, 1024));
  let evenNuls = 0;
  let oddNuls = 0;
  for (let i = 0; i < sample.length; i++) {
    if (sample[i] === 0x00) (i % 2 === 0 ? evenNuls++ : oddNuls++);
  }
  const nulFraction = (evenNuls + oddNuls) / (sample.length || 1);
  if (nulFraction > 0.2) {
    // Little-endian puts the NUL in the odd byte of each ASCII pair.
    return new TextDecoder(oddNuls >= evenNuls ? 'utf-16le' : 'utf-16be').decode(bytes);
  }

  return new TextDecoder('utf-8').decode(bytes);
}

/**
 * Read raw CSV text into delimiter-detected, quote-aware records. Shared by the
 * deterministic parser here and the modular parser in `ami/parser.ts` so both
 * agree on where rows and fields begin.
 */
export function readCSVLines(text: string): { delimiter: string; lines: string[] } {
  // The header is a physical line in every export we have seen, so it is safe to
  // read the delimiter off it before quote-aware record splitting can run.
  const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
  const delimiter = detectDelimiter(firstLine);
  const lines = splitCSVRecords(text, delimiter).filter(l => l.trim() !== "");
  return { delimiter, lines };
}

const STRICT_NUMERIC_RE = /^[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?(?:\s*(?:kwh|kvarh|kvah|kva|kvar|kw|wh|w))?$/i;

/**
 * Parse a cell that is a number, and only a number.
 *
 * `parseFloat` stops at the first character it cannot use and returns whatever
 * it read, so it reports a street address as a number ("12 POINT VIEW DR" -> 12)
 * and a US-format date as its month ("8/13/2026 0:00" -> 8). The column profiler
 * used it to decide which columns hold numbers, so on a National Grid export the
 * address and `interval_end` columns both looked numeric in the 0.05–200 band and
 * outranked the real `interval_kWh` column — the reported peak demand was the
 * month number of the latest reading.
 *
 * A trailing unit is tolerated because some exports write "1.23 kW" in the value
 * cell; anything else non-numeric makes the cell not a number.
 */
export function parseStrictNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value === null || value === undefined) return null;
  const str = String(value).trim().replace(/,/g, "");
  if (str === "" || !STRICT_NUMERIC_RE.test(str)) return null;
  const n = parseFloat(str);
  return Number.isFinite(n) ? n : null;
}

export function detectIntervalMinutes(data: ParsedDataPoint[]): number | null {
  if (data.length < 2) return null;

  const sorted = [...data].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const intervals: number[] = [];
  for (let i = 1; i < Math.min(sorted.length, 10); i++) {
    const diff = new Date(sorted[i].timestamp).getTime() - new Date(sorted[i - 1].timestamp).getTime();
    intervals.push(diff / (1000 * 60));
  }

  const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
  return Math.round(avgInterval);
}
